import { useState, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Breadcrumbs,
  Link,
  Skeleton,
  Grid,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import { trpc } from '../../lib/trpc';
import { MonitorHeader } from '../../components/monitors/MonitorHeader';
import { ResponseTimeChart } from '../../components/monitors/ResponseTimeChart';
import { CheckHistory } from '../../components/monitors/CheckHistory';
import { UptimeBar } from '../../components/ui/UptimeBar';
import type { MonitorStatus } from '../../components/ui/StatusBadge';
import type { MonitorType } from '../../components/ui/MonitorTypeBadge';

/**
 * Stats card component for displaying metrics
 */
interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  isLoading?: boolean;
}

function StatsCard({ title, value, subtitle, icon, color, isLoading }: StatsCardProps) {
  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 2.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${color}15`,
            color: color,
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      {isLoading ? (
        <Skeleton variant="text" width={80} height={36} />
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 700, color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}

/**
 * Convert hours to time range label for chart
 */
type TimeRange = '24h' | '7d' | '30d';

function timeRangeToHours(range: TimeRange): number {
  switch (range) {
    case '24h':
      return 24;
    case '7d':
      return 168; // 7 * 24
    case '30d':
      return 720; // 30 * 24
    default:
      return 24;
  }
}

/**
 * Monitor detail page component
 * Displays detailed information about a single monitor
 */
export function MonitorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Chart time range state
  const [chartTimeRange, setChartTimeRange] = useState<TimeRange>('24h');

  // Pagination state for checks
  const [checksPage, setChecksPage] = useState(1);
  const [checksPageSize, setChecksPageSize] = useState(25);

  // Fetch monitor data
  const {
    data: monitor,
    isLoading: isLoadingMonitor,
    error: monitorError,
    refetch: refetchMonitor,
  } = trpc.monitors.getById.useQuery(
    { id: id! },
    {
      enabled: !!id,
    }
  );

  // Fetch uptime summary
  const { data: uptimeSummary, isLoading: isLoadingUptimeSummary } =
    trpc.checks.getUptimeSummary.useQuery(
      { monitorId: id! },
      {
        enabled: !!id,
      }
    );

  // Fetch checks for table
  // Fetch failed checks only
  const { data: checksData, isLoading: isLoadingChecks } = trpc.checks.getByMonitor.useQuery(
    { monitorId: id!, page: checksPage, pageSize: checksPageSize, status: 'DOWN' },
    {
      enabled: !!id,
    }
  );

  // Fetch response time chart data
  const { data: chartData, isLoading: isLoadingChart } = trpc.checks.getResponseTimeChart.useQuery(
    { monitorId: id!, hours: timeRangeToHours(chartTimeRange) },
    {
      enabled: !!id,
    }
  );

  // Fetch recent checks for uptime bar (90 days)
  const { data: recentChecks, isLoading: isLoadingRecentChecks } = trpc.checks.getRecent.useQuery(
    { monitorId: id!, limit: 200 },
    {
      enabled: !!id,
    }
  );

  // Mutations
  const pauseMutation = trpc.monitors.pause.useMutation({
    onSuccess: () => {
      refetchMonitor();
    },
  });

  const resumeMutation = trpc.monitors.resume.useMutation({
    onSuccess: () => {
      refetchMonitor();
    },
  });

  const deleteMutation = trpc.monitors.delete.useMutation({
    onSuccess: () => {
      navigate('/monitors');
    },
  });

  // Calculate quick stats
  const quickStats = useMemo(() => {
    if (!uptimeSummary) {
      return {
        uptimePercentage: null,
        avgResponseTime: null,
        checksToday: 0,
      };
    }

    return {
      uptimePercentage: uptimeSummary.uptime.last30Days,
      avgResponseTime: uptimeSummary.avgResponseTime.last24Hours,
      checksToday: monitor?.totalChecks ?? 0, // Will use today's count if available
    };
  }, [uptimeSummary, monitor]);

  // Handle actions
  const handlePause = () => {
    if (id) {
      pauseMutation.mutate({ id });
    }
  };

  const handleResume = () => {
    if (id) {
      resumeMutation.mutate({ id });
    }
  };

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate({ id });
    }
  };

  const handleChartTimeRangeChange = (range: TimeRange) => {
    setChartTimeRange(range);
  };

  const handleChecksPageChange = (page: number) => {
    setChecksPage(page);
  };

  const handleChecksPageSizeChange = (pageSize: number) => {
    setChecksPageSize(pageSize);
    setChecksPage(1); // Reset to first page
  };

  // Loading state
  const isLoading = isLoadingMonitor;

  if (isLoading) {
    return (
      <Box>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
            Monitors
          </Link>
          <Skeleton variant="text" width={100} />
        </Breadcrumbs>

        {/* Header Skeleton */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="rounded" width={80} height={32} />
                <Skeleton variant="text" width={200} height={40} />
              </Box>
              <Skeleton variant="text" width={300} height={24} />
              <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                <Skeleton variant="text" width={100} height={20} />
                <Skeleton variant="text" width={100} height={20} />
                <Skeleton variant="text" width={100} height={20} />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Stats Skeleton */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>

        {/* Uptime Bar Skeleton */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={48} />
        </Paper>

        {/* Chart Skeleton */}
        <Skeleton variant="rounded" height={300} sx={{ mb: 3 }} />

        {/* Table Skeleton */}
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  // Error state
  if (monitorError) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
            Monitors
          </Link>
          <Typography color="text.primary">Monitor Details</Typography>
        </Breadcrumbs>

        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load monitor. {monitorError.message}
        </Alert>

        <Button
          component={RouterLink}
          to="/monitors"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
        >
          Back to Monitors
        </Button>
      </Box>
    );
  }

  // Not found state
  if (!monitor) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
            Monitors
          </Link>
          <Typography color="text.primary">Monitor Details</Typography>
        </Breadcrumbs>

        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Monitor not found
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            The monitor with ID &quot;{id}&quot; could not be found.
          </Typography>
          <Button
            component={RouterLink}
            to="/monitors"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
          >
            Back to Monitors
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
          Monitors
        </Link>
        <Typography color="text.primary">{monitor.name}</Typography>
      </Breadcrumbs>

      {/* Monitor Header */}
      <Box sx={{ mb: 3 }}>
        <MonitorHeader
          monitor={{
            id: monitor.id,
            name: monitor.name,
            url: monitor.url,
            type: monitor.type as MonitorType,
            status: monitor.status as MonitorStatus,
            interval: monitor.interval,
            lastCheckedAt: monitor.lastCheckedAt,
          }}
          stats={quickStats}
          onPause={handlePause}
          onResume={handleResume}
          onDelete={handleDelete}
          isPausing={pauseMutation.isPending}
          isDeleting={deleteMutation.isPending}
        />
      </Box>

      {/* Stats Cards Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Uptime 24h */}
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Uptime (24h)"
            value={
              uptimeSummary?.uptime.last24Hours != null
                ? `${uptimeSummary.uptime.last24Hours.toFixed(2)}%`
                : '-'
            }
            icon={<TrendingUpIcon fontSize="small" />}
            color={
              uptimeSummary?.uptime.last24Hours == null
                ? '#6B7280'
                : uptimeSummary.uptime.last24Hours >= 99
                  ? '#10B981'
                  : uptimeSummary.uptime.last24Hours >= 95
                    ? '#F59E0B'
                    : '#EF4444'
            }
            isLoading={isLoadingUptimeSummary}
          />
        </Grid>

        {/* Uptime 7d */}
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Uptime (7d)"
            value={
              uptimeSummary?.uptime.last7Days != null
                ? `${uptimeSummary.uptime.last7Days.toFixed(2)}%`
                : '-'
            }
            icon={<TrendingUpIcon fontSize="small" />}
            color={
              uptimeSummary?.uptime.last7Days == null
                ? '#6B7280'
                : uptimeSummary.uptime.last7Days >= 99
                  ? '#10B981'
                  : uptimeSummary.uptime.last7Days >= 95
                    ? '#F59E0B'
                    : '#EF4444'
            }
            isLoading={isLoadingUptimeSummary}
          />
        </Grid>

        {/* Uptime 30d */}
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Uptime (30d)"
            value={
              uptimeSummary?.uptime.last30Days != null
                ? `${uptimeSummary.uptime.last30Days.toFixed(2)}%`
                : '-'
            }
            icon={<TrendingUpIcon fontSize="small" />}
            color={
              uptimeSummary?.uptime.last30Days == null
                ? '#6B7280'
                : uptimeSummary.uptime.last30Days >= 99
                  ? '#10B981'
                  : uptimeSummary.uptime.last30Days >= 95
                    ? '#F59E0B'
                    : '#EF4444'
            }
            isLoading={isLoadingUptimeSummary}
          />
        </Grid>

        {/* Avg Response Time */}
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Avg Response (24h)"
            value={
              uptimeSummary?.avgResponseTime.last24Hours != null
                ? `${Math.round(uptimeSummary.avgResponseTime.last24Hours)}ms`
                : '-'
            }
            icon={<SpeedIcon fontSize="small" />}
            color="#3B82F6"
            isLoading={isLoadingUptimeSummary}
          />
        </Grid>
      </Grid>

      {/* Uptime History (30 days) */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Uptime History
        </Typography>
        {isLoadingRecentChecks ? (
          <Skeleton variant="rounded" height={48} />
        ) : (
          <UptimeBar
            checks={(recentChecks ?? []).map((check) => ({
              status: check.status,
              createdAt: check.createdAt,
            }))}
            days={30}
          />
        )}
      </Paper>

      {/* Response Time Chart */}
      <Box sx={{ mb: 3 }}>
        <ResponseTimeChart
          data={chartData ?? []}
          isLoading={isLoadingChart}
          onTimeRangeChange={handleChartTimeRangeChange}
          initialTimeRange={chartTimeRange}
          height={300}
        />
      </Box>

      {/* Recent Failed Checks */}
      <CheckHistory
        checks={(checksData?.checks ?? []).map((check) => ({
          id: check.id,
          status: check.status,
          responseTime: check.responseTime,
          statusCode: check.statusCode,
          error: check.error,
          createdAt: check.createdAt,
        }))}
        {...(checksData?.pagination && { pagination: checksData.pagination })}
        onPageChange={handleChecksPageChange}
        onRowsPerPageChange={handleChecksPageSizeChange}
        isLoading={isLoadingChecks}
        title="Recent Failed Checks"
        emptyMessage="No failed checks recorded"
      />
    </Box>
  );
}

export default MonitorDetailPage;
